import {
  IActiveCollection,
  IGetProductsResult,
  IGetProductsResultsQuery,
  ISearchAndFilterProduct,
  ISearchAndFilterProductImage,
  ISearchAndFilterProductPrice,
  ISearchFilter,
  ISearchProviderConfig,
} from '../../../types';
import standardisePrice from '../../../../../util/standardisePrice';

/**
 * GetProductResults
 *
 * Calls a theme JSON view backed by Shopify Search & Discovery to
 * retrieve products and filters for PLP. The Liquid view should live at:
 *
 *   /search?view=app-layer-search
 *
 * and return data shaped like:
 *   { products: [...], total: number, records: number, filters: [...] }
 */
const GetProductResults = (
  config: ISearchProviderConfig,
  searchFilters: ISearchFilter[],
  activeCollection: IActiveCollection,
  query: IGetProductsResultsQuery,
): Promise<IGetProductsResult> =>
  new Promise<IGetProductsResult>((resolve) => {
    const response: IGetProductsResult = {
      records: 0,
      total: 0,
      raw: null,
      products: [],
      filters: [],
    };

    let pageIndex = 1;
    if (config.rLoadMoreMode === 'paginate' && query.pageIndex) {
      pageIndex = query.pageIndex;
    }

    let limit = config.rProductsPerPage;
    if (config.rLoadMoreMode === 'loadMore' && query.pageIndex) {
      limit = limit * query.pageIndex;
    }

    try {
      const params: Record<string, string> = {
        q: query.term ?? '',
        collection_id: activeCollection.id ?? '',
        collection_handle: activeCollection.handle ?? '',
        limit: String(limit),
        page: String(pageIndex),
      };

      if (query.sortKey) {
        params.sort_key = query.sortKey;
      }

      // encode filters to be interpreted by the Liquid view
      searchFilters.forEach((filter) => {
        filter.options.forEach((option: any) => {
          if (!option.selected) return;

          if (['MULTI_OPTION', 'SINGLE_OPTION'].includes(filter.type)) {
            const key = `filter_${filter.identifier}`;
            if (!params[key]) params[key] = '';
            params[key] = params[key]
              ? `${params[key]},${option.identifier}`
              : option.identifier;
          } else if (filter.type === 'RANGE') {
            const key = `filter_${filter.identifier}`;
            params[key] = `${option.selectedMinimum}:${option.selectedMaximum}`;
          }
        });
      });

      // On collection templates we want to scope results + filters to the
      // active collection. For search templates we fall back to the /search
      // endpoint.
      const basePath =
        activeCollection && activeCollection.handle
          ? `/collections/${activeCollection.handle}`
          : '/search';

      const url = `${basePath}?view=app-layer-search&${new URLSearchParams(
        params,
      ).toString()}`;

      fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      })
        .then((r) => r.text())
        .then((text) => {
          try {
            const data = JSON.parse(text);
            response.raw = data;

            response.total = data.total ?? 0;
            response.records = Array.isArray(data.products)
              ? data.products.length
              : 0;

            // Map products
            if (Array.isArray(data.products)) {
              response.products = data.products.map((product: any) => {
                const priceMin = standardisePrice(product.price_min || product.price);
                const priceMax = standardisePrice(product.price_max || product.price);
                const originalPrice = product.compare_at_price_max
                  ? standardisePrice(product.compare_at_price_max)
                  : priceMax;

                return <ISearchAndFilterProduct>{
                  id: String(product.id),
                  name: product.title,
                  handle: product.handle,
                  tags: product.tags || [],
                  url: `https://${location.hostname}/products/${product.handle}`,
                  images: (product.images || []).map(
                    (image: any): ISearchAndFilterProductImage => ({
                      src: typeof image === 'string' ? image : image.src,
                    }),
                  ),
                  price: <ISearchAndFilterProductPrice>{
                    priceMin,
                    priceMax,
                    finalPrice: priceMax,
                    originalPrice,
                  },
                  meta: product.meta || {},
                  variants: product.variants || [],
                };
              });
            }

            // Filters are passed through from the Liquid view in app-layer format
            if (Array.isArray(data.filters)) {
              response.filters = data.filters as ISearchFilter[];
            }
          } catch (parseError) {
            console.error('ShopifySearch:GetProductResults JSON parse error', {
              url,
              preview: text ? text.substring(0, 200) : '',
              error: parseError,
            });
          }

          resolve(response);
        })
        .catch((e) => {
          console.error('ShopifySearch:GetProductResults request error', {
            url,
            error: e,
          });
          resolve(response);
        });
    } catch (e) {
      console.error(e);
      resolve(response);
    }
  });

export default GetProductResults;

